import { z } from 'zod';
import { DateSchema, ObjectIdHexStringSchema, ObjectIdSchema } from './base';

const MAX_LABEL_INDEX = 0xffffff;

/** A label's printed name: its index as 6 hex digits, e.g. `A020F1`. */
const LabelNameSchema = z
  .string()
  .regex(/^[0-9A-F]{6}$/i, 'Expected a 6-digit hex label name')
  .transform((it) => Number.parseInt(it, 16));

export const IndexSpaceQuerySchema = z
  .object({
    start: LabelNameSchema.optional(),
    count: z.coerce.number().int().min(1).max(10_000).optional(),
  })
  .refine((it) => (it.start == null) === (it.count == null), 'start and count go together')
  .refine(
    (it) => it.start == null || it.count == null || it.start + it.count - 1 <= MAX_LABEL_INDEX,
    'Range runs past FFFFFF',
  );

export const IndexSpaceSchema = z.object({
  nextFreeIndex: z.string(),
  /** Label names inside the requested range that already have a Tracker. */
  conflicts: z.array(z.string()),
});

export type IndexSpace = z.infer<typeof IndexSpaceSchema>;

export const TrackerLabelTemplateSchema = z.object({
  file: z.string(),
  name: z.string(),
});

export type TrackerLabelTemplate = z.infer<typeof TrackerLabelTemplateSchema>;

/** Labels per printing. Keeps the generate request (#3775) inside the nginx proxy timeout. */
export const MAX_LABELS_PER_PRINTING = 1000;

/** The template a printing gets when it names none. */
export const DEFAULT_TRACKER_LABEL_TEMPLATE = 'gl320_template.svg';

export const PRINTING_STATES = ['planned', 'printed', 'trackersAdded'] as const;

const LabelRangeSchema = z.object({ first: z.string(), last: z.string() });

export type LabelRange = z.infer<typeof LabelRangeSchema>;

export const TrackerLabelPrintingSchema = z.object({
  _id: ObjectIdSchema,
  /** null only for printings seeded from the generator README */
  owner: ObjectIdSchema.nullable(),
  tagId: ObjectIdSchema.nullable(),
  template: z.string(),
  ranges: z.array(LabelRangeSchema),
  note: z.string(),
  state: z.enum(PRINTING_STATES),
  printedAt: DateSchema.optional(),
  trackersAddedAt: DateSchema.optional(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  labelCount: z.number(),
});

export type TrackerLabelPrinting = z.infer<typeof TrackerLabelPrintingSchema>;

export const ReservePrintingBodySchema = z
  .object({
    indices: z.array(LabelNameSchema).min(1).optional(),
    start: LabelNameSchema.optional(),
    count: z.number().int().min(1).max(MAX_LABELS_PER_PRINTING).optional(),
    ownerId: ObjectIdHexStringSchema,
    template: z.string().optional(),
    note: z.string().optional(),
  })
  .refine(
    (it) => (it.indices == null) !== (it.start == null && it.count == null),
    'Give either indices or start and count',
  )
  .refine((it) => (it.start == null) === (it.count == null), 'start and count go together');

export type ReservePrintingBody = z.input<typeof ReservePrintingBodySchema>;

export const PrintingNotePatchSchema = z.strictObject({ note: z.string() });
